/**
 * Live Strategy Calculator
 * Real-time race strategy calculations based on live telemetry data
 * 
 * IMSA Multi-Class Race Rules (iRacing):
 * - The race is timed, not a fixed lap count
 * - The overall leader (GTP/LMDh = fastest class) dictates when the race ends
 * - White flag shown when the leader crosses S/F with remaining time < leader's lap time
 * - ALL classes get checkered when the leader finishes — slower classes complete fewer laps
 * - Each class calculates its own lap count based on when the leader finishes
 * 
 * Pit Stop Rules:
 * - Fueling and tire changes happen simultaneously
 * - Full fuel stop (100%) always includes tires (tires finish before fueling)
 * - Splash & go (partial fuel) = no tires (fueling is faster than tire change)
 * - Two pit types: full fuel + tires vs splash & go (fuel only)
 * 
 * Strategy Decision Framework:
 * The calculator evaluates multiple strategy options and picks the one with the
 * lowest total time cost. Options include:
 *   A) No action — fuel is sufficient
 *   B) Save fuel only — avoid all remaining pit stops
 *   C) Save fuel to eliminate one stop — reduce N stops to N-1
 *   D) Splash & go — short pit for partial fuel, no tires
 *   E) Splash & go + mild save — combine a short pit with light saving
 *   F) Full pit stop(s) — fill tank + free tires
 * 
 * Each option's total time cost is calculated and compared. The cheapest wins.
 */

import type { FuelSavingCalibration } from "../types/strategyCalibration";
import { buildFuelSavingModel } from "./fuelSavingCalibration";

export type CarClass = 'GTP' | 'LMP2' | 'GT3';

export interface LiveStrategyInput {
  // Current telemetry data
  currentLap: number;
  currentFuel: number;
  avgLapTime: number;
  avgFuelPerLap: number;
  
  // Race parameters
  raceDurationMinutes: number;
  fuelCapacity: number;
  isMultiClass: boolean;
  fastestClassLapTime?: number; // P1 lap time (fastest class / GTP)
  
  // Car class for class-specific strategy
  carClass?: CarClass;
  fuelSavingCalibration?: FuelSavingCalibration;
  
  // Pit stop time deltas (entry-to-exit vs staying on track)
  pitStopTime?: number;          // Full fuel + tires delta (seconds). Default varies by class.
  pitStopSplashTime?: number;    // Splash & go (fuel only, no tires) delta (seconds). Default varies by class.

  // Detailed pit stop breakdown (when available, enables dynamic pit time calculation)
  pitInTime?: number;            // Time to enter pit lane (seconds)
  pitOutTime?: number;           // Time to exit pit lane (seconds)
  refuelRate?: number;           // Seconds per liter of fuel added (derived from fuelingTime / fuelCapacity)
}

/**
 * A candidate strategy option with its total time cost.
 * The calculator evaluates all feasible options and picks the cheapest.
 */
export interface StrategyOption {
  type: 'no_action' | 'save_only' | 'save_to_skip_stop' | 'splash_only' | 'splash_and_save' | 'full_stop';
  totalTimeCost: number;          // total seconds lost vs driving at normal pace with no stops
  pitStops: number;               // number of pit stops in this plan
  fuelSavePercent: number;        // fuel saving % required (0 if none)
  fuelSaveTimeCost: number;       // total seconds lost from fuel saving
  pitTimeCost: number;            // total seconds lost from pit stops
  pitType: 'full' | 'splash' | null;
  description: string;            // human-readable summary
  fuelToAdd: number | null;       // liters to add at next pit (null if no pit)
  targetFuelPerLap: number | null; // target fuel consumption per lap if saving (null if no saving)
  recommendedPitLap: number | null;
}

export interface LiveStrategyResult {
  // Race completion info
  totalRaceLaps: number;        // Total laps the overall LEADER (GTP) will complete
  myTotalLaps: number;          // Total laps YOUR CLASS will complete
  lapsRemaining: number;        // Laps remaining for YOUR CAR
  raceProgress: number;         // 0-100%
  estimatedRaceTimeRemaining: number; // seconds remaining for your class
  
  // Fuel calculations
  currentFuelLaps: number;      // laps you can complete with current fuel
  fuelNeededToFinish: number;   // liters needed to finish without stopping
  fuelDeficit: number;          // liters (negative = surplus, positive = need more)
  
  // Strategy recommendations (derived from best evaluated option)
  needsFuelSaving: boolean;
  fuelSavingTarget: number;     // liters per lap to save
  targetFuelPerLap: number;     // new target consumption
  fuelSavingPercentage: number; // % reduction needed
  
  // Pit stop predictions
  needsPitStop: boolean;
  pitStopsRemaining: number;    // estimated remaining pit stops
  recommendedPitLap: number | null;
  fuelToAdd: number | null;     // liters (null = no pit needed)
  pitStopType: 'full' | 'splash' | null; // full = 100% fuel + tires, splash = partial fuel only
  
  // Strategy comparison — all evaluated options, sorted best to worst
  bestStrategy: StrategyOption;
  allStrategies: StrategyOption[];
  
  // Status messages
  status: 'ok' | 'saving_required' | 'pit_required' | 'insufficient_data';
  message: string;
}

export class LiveStrategyCalculator {

  // ─── Class-Specific Constants ─────────────────────────────────────────

  /**
   * Class-specific maximum realistic fuel saving percentage.
   * Beyond these limits, lap time loss makes fuel saving worse than pitting.
   * 
   * - GTP (LMDh): Hybrid energy recovery during lift-and-coast makes saving very effective
   * - LMP2: No hybrid system, purely ICE — fuel saving is less effective
   * - GT3: Heavy braking zones allow good lift-and-coast opportunities
   */
  private getDefaultMaxFuelSavePercent(carClass?: CarClass): number {
    switch (carClass) {
      case 'GTP':  return 15;
      case 'LMP2': return 12;
      case 'GT3':  return 15;
      default:     return 15;
    }
  }

  /**
   * Default pit stop time delta by class (full fuel + tires).
   * Tires are free when refueling to 100% — fueling takes longer than tire change.
   */
  private getDefaultPitStopTime(carClass?: CarClass): number {
    switch (carClass) {
      case 'GTP':  return 55;
      case 'LMP2': return 55;
      case 'GT3':  return 62;
      default:     return 60;
    }
  }

  /**
   * Default splash & go time delta by class (partial fuel, NO tires).
   * Much shorter than full stop — only in pit lane long enough for a few liters.
   */
  private getDefaultSplashTime(carClass?: CarClass): number {
    switch (carClass) {
      case 'GTP':  return 30;
      case 'LMP2': return 30;
      case 'GT3':  return 35;
      default:     return 32;
    }
  }

  /**
   * Class-specific lap time penalty per 1% fuel saving (seconds per lap).
   * 
   * - GTP (LMDh): Hybrid recovery partially offsets the lift-and-coast time loss (~0.10s)
   * - LMP2: No hybrid, full aero penalty from lifting early (~0.18s)
   * - GT3: Moderate penalty, braking zones help (~0.13s)
   */
  private getDefaultFuelSaveLapTimePenalty(carClass?: CarClass): number {
    switch (carClass) {
      case 'GTP':  return 0.10;
      case 'LMP2': return 0.18;
      case 'GT3':  return 0.13;
      default:     return 0.15;
    }
  }

  // ─── Multi-Class Lap Calculation ──────────────────────────────────────

  /**
   * Calculate how many laps each class will complete before the race ends.
   * 
   * IMSA Multi-Class End-of-Race Logic:
   * 1. The overall leader (GTP) dictates the white flag for ALL classes
   * 2. White flag when leader crosses S/F and remaining time < leader's lap time
   * 3. Leader completes one more lap → checkered flag for EVERYONE
   * 4. All other classes get checkered at that same moment
   * 5. Slower classes complete fewer laps (they were on a different lap count)
   * 
   * Example (6h race):
   *   GTP: 1:35 lap → floor(21600/95)+1 = 228 laps, race ends at 228×95 = 21,660s
   *   LMP2: 1:42 lap → floor(21660/102) = 212 laps
   *   GT3: 1:55 lap → floor(21660/115) = 188 laps
   * 
   * For single-class or if you ARE the leader class:
   *   Standard: floor(raceTime / lapTime) + 1
   */
  private calculateMyTotalLaps(
    raceDurationMinutes: number,
    myLapTime: number,
    isMultiClass: boolean,
    fastestClassLapTime?: number
  ): { leaderLaps: number; myLaps: number } {
    const raceSeconds = raceDurationMinutes * 60;

    // Determine leader lap time
    const leaderLapTime = isMultiClass && fastestClassLapTime
      ? fastestClassLapTime
      : myLapTime;
    
    // Leader total laps (white flag rule: floor + 1)
    const leaderLaps = Math.floor(raceSeconds / leaderLapTime) + 1;
    
    // Actual race end time (when leader takes checkered flag)
    const raceEndTime = leaderLaps * leaderLapTime;
    
    if (!isMultiClass || !fastestClassLapTime || myLapTime <= fastestClassLapTime) {
      // You are the leader class, or single class race
      return { leaderLaps, myLaps: leaderLaps };
    }

    // For slower classes: how many of MY laps fit before the leader finishes?
    // floor() because you won't complete a lap that's still in progress when checkered drops
    const myLaps = Math.floor(raceEndTime / myLapTime);

    return { leaderLaps, myLaps };
  }

  // ─── Fuel Saving Time Cost ────────────────────────────────────────────

  /**
   * Calculate the total time cost of a given fuel saving percentage over N laps.
   * Returns the total seconds lost from running at reduced pace.
   */
  private getFuelSavingModel(input: LiveStrategyInput) {
    return buildFuelSavingModel({
      calibration: input.fuelSavingCalibration,
      defaultMaxFuelSavePercent: this.getDefaultMaxFuelSavePercent(input.carClass),
      defaultPenaltyPerPercent: this.getDefaultFuelSaveLapTimePenalty(input.carClass),
    });
  }

  private calculateFuelSaveTimeCost(savePercent: number, laps: number, penaltyPerPercent: number): number {
    return savePercent * penaltyPerPercent * laps;
  }

  // ─── Strategy Option Evaluator ────────────────────────────────────────

  /**
   * Evaluate all feasible strategy options and return them sorted by total time cost.
   * 
   * This is the core strategy engine. It considers:
   *   A) No action — if fuel is already sufficient
   *   B) Save fuel only — to finish without any pit stops
   *   C) Save fuel to skip one stop — reduce N stops to N-1 (proactive saving)
   *   D) Splash & go only — short pit, no saving required
   *   E) Splash & go + mild saving — combine a short pit with light saving to reach the end
   *   F) Full stop(s) — baseline pit strategy at normal pace
   * 
   * Each option is only added if it's physically feasible (save % within class limits,
   * fuel fits in tank, etc.). The cheapest option wins.
   */
  private evaluateStrategies(
    input: LiveStrategyInput,
    lapsRemaining: number,
    currentFuelLaps: number,
    fuelDeficit: number,
    myTotalLaps: number,
  ): StrategyOption[] {
    const options: StrategyOption[] = [];
    const fuelSavingModel = this.getFuelSavingModel(input);
    const maxFuelSave = fuelSavingModel.maxFuelSavePercent;
    const penaltyPerPercent = fuelSavingModel.lapTimePenaltyPerPercent;
    const fullPitTime = input.pitStopTime ?? this.getDefaultPitStopTime(input.carClass);
    const splashPitTime = input.pitStopSplashTime ?? this.getDefaultSplashTime(input.carClass);
    const maxStintLaps = Math.floor(input.fuelCapacity / input.avgFuelPerLap);
    const safetyMargin = 2; // always keep 2 laps of fuel as safety buffer

    if (lapsRemaining <= 0) {
      options.push({
        type: 'no_action', totalTimeCost: 0, pitStops: 0,
        fuelSavePercent: 0, fuelSaveTimeCost: 0, pitTimeCost: 0,
        pitType: null, description: 'Race complete.',
        fuelToAdd: null, targetFuelPerLap: null, recommendedPitLap: null,
      });
      return options;
    }

    // ── Option A: No action (fuel already sufficient) ───────────────────
    if (fuelDeficit <= 0) {
      options.push({
        type: 'no_action', totalTimeCost: 0, pitStops: 0,
        fuelSavePercent: 0, fuelSaveTimeCost: 0, pitTimeCost: 0,
        pitType: null,
        description: `On track to finish with ${Math.abs(fuelDeficit).toFixed(1)}L surplus.`,
        fuelToAdd: null, targetFuelPerLap: null, recommendedPitLap: null,
      });
    }

    // ── Option B: Save fuel only (finish without any pit stops) ─────────
    // Possible if: we have SOME fuel and saving % is within class limits
    if (fuelDeficit > 0 && lapsRemaining > 0 && input.currentFuel > 0) {
      const targetPerLap = input.currentFuel / lapsRemaining;
      const savePercent = ((input.avgFuelPerLap - targetPerLap) / input.avgFuelPerLap) * 100;

      if (savePercent > 0 && savePercent <= maxFuelSave) {
        const timeCost = this.calculateFuelSaveTimeCost(savePercent, lapsRemaining, penaltyPerPercent);
        options.push({
          type: 'save_only', totalTimeCost: timeCost, pitStops: 0,
          fuelSavePercent: savePercent, fuelSaveTimeCost: timeCost, pitTimeCost: 0,
          pitType: null,
          description: `Save ${savePercent.toFixed(1)}% fuel (${targetPerLap.toFixed(2)}L/lap) for ${lapsRemaining} laps. ` +
                       `Time cost: ${timeCost.toFixed(1)}s. No pit stop needed.`,
          fuelToAdd: null, targetFuelPerLap: targetPerLap, recommendedPitLap: null,
        });
      }
    }

    // ── Baseline pit stops needed at current pace ───────────────────────
    const baselinePitStops = fuelDeficit > 0 ? Math.ceil(fuelDeficit / input.fuelCapacity) : 0;

    // ── Option C: Save fuel proactively to eliminate one pit stop ────────
    // If we need N stops, check if saving X% could reduce that to N-1.
    // This works even when fuelDeficit <= 0 (proactive: avoid a future stop).
    // 
    // Logic: With N-1 stops, we get (N-1) × fuelCapacity extra fuel.
    // Total fuel available = currentFuel + (N-1) × fuelCapacity
    // Required: totalFuelAvailable >= lapsRemaining × targetFuelPerLap
    // => targetFuelPerLap <= (currentFuel + (N-1) × fuelCapacity) / lapsRemaining
    if (baselinePitStops >= 1 && lapsRemaining > 0) {
      const reducedStops = baselinePitStops - 1;
      const totalFuelWithReducedStops = input.currentFuel + reducedStops * input.fuelCapacity;
      const targetPerLap = totalFuelWithReducedStops / lapsRemaining;
      const savePercent = ((input.avgFuelPerLap - targetPerLap) / input.avgFuelPerLap) * 100;

      if (savePercent > 0 && savePercent <= maxFuelSave) {
        // Verify stint feasibility: can we reach each pit on the reduced plan?
        // With saving, current fuel lasts: currentFuel / targetPerLap laps
        const currentStintLaps = Math.floor(input.currentFuel / targetPerLap);
        
        if (currentStintLaps > safetyMargin) {
          const savingCost = this.calculateFuelSaveTimeCost(savePercent, lapsRemaining, penaltyPerPercent);
          // We're saving one full pit stop delta
          const pitSaved = fullPitTime;
          // But still pay for the reduced number of stops
          const remainingPitCost = reducedStops * fullPitTime;
          const totalTimeCost = savingCost + remainingPitCost;

          // Calculate optimal pit lap for the reduced-stop plan
          let recPitLap: number | null = null;
          let recFuelToAdd: number | null = null;
          if (reducedStops > 0) {
            // Pit at the end of the current stint (with safety buffer)
            const firstPitIn = Math.max(safetyMargin + 1, currentStintLaps - safetyMargin);
            recPitLap = input.currentLap + firstPitIn;
            const lapsAfterPit = myTotalLaps - recPitLap;
            const fuelInTankAtPit = Math.max(0, input.currentFuel - firstPitIn * targetPerLap);
            recFuelToAdd = Math.min(
              input.fuelCapacity - fuelInTankAtPit,
              Math.max(0, lapsAfterPit * targetPerLap - fuelInTankAtPit)
            );
          }

          options.push({
            type: 'save_to_skip_stop', totalTimeCost, pitStops: reducedStops,
            fuelSavePercent: savePercent, fuelSaveTimeCost: savingCost, pitTimeCost: remainingPitCost,
            pitType: reducedStops > 0 ? 'full' : null,
            description: `Save ${savePercent.toFixed(1)}% to skip 1 stop (${baselinePitStops}→${reducedStops}). ` +
                         `Saving costs ${savingCost.toFixed(1)}s, skipping saves ${pitSaved.toFixed(0)}s. ` +
                         `Net ${pitSaved - savingCost > 0 ? 'gain' : 'loss'}: ${Math.abs(pitSaved - savingCost).toFixed(1)}s.`,
            fuelToAdd: recFuelToAdd,
            targetFuelPerLap: targetPerLap,
            recommendedPitLap: recPitLap,
          });
        }
      }
    }

    // ── Option D: Splash & go only (no fuel saving) ─────────────────────
    // Works when: deficit is small enough that a partial refuel covers it,
    // and the remaining laps after the splash fit within a single stint.
    if (fuelDeficit > 0 && fuelDeficit <= input.fuelCapacity * 0.5) {
      const pitLap = input.currentLap + Math.max(1, Math.floor(currentFuelLaps - safetyMargin));
      // Don't splash after the race
      if (pitLap < myTotalLaps) {
        const lapsAfterPit = myTotalLaps - pitLap;
        const fuelInTankAtPit = Math.max(0, safetyMargin * input.avgFuelPerLap);
        const fuelToAddRaw = lapsAfterPit * input.avgFuelPerLap - fuelInTankAtPit;
        
        // Splash only works if the amount fits as a partial fill (< 50% tank)
        if (fuelToAddRaw > 0 && fuelToAddRaw <= input.fuelCapacity * 0.5 && lapsAfterPit <= maxStintLaps) {
          options.push({
            type: 'splash_only', totalTimeCost: splashPitTime, pitStops: 1,
            fuelSavePercent: 0, fuelSaveTimeCost: 0, pitTimeCost: splashPitTime,
            pitType: 'splash',
            description: `Splash & go on lap ${pitLap}: add ${fuelToAddRaw.toFixed(1)}L (no tires). ~${splashPitTime}s delta.`,
            fuelToAdd: fuelToAddRaw,
            targetFuelPerLap: null,
            recommendedPitLap: pitLap,
          });
        }
      }
    }

    // ── Option E: Splash & go + mild save ───────────────────────────────
    // Combine a small splash with light saving. This can be cheaper than
    // a full stop OR heavy saving alone.
    // Logic: Do a splash part-way through, then save enough to make
    // the remaining laps on partial refuel.
    if (fuelDeficit > 0 && lapsRemaining > safetyMargin) {
      const pitLap = input.currentLap + Math.max(1, Math.floor(currentFuelLaps - safetyMargin));
      if (pitLap < myTotalLaps) {
        const lapsAfterPit = myTotalLaps - pitLap;
        const fuelInTankAtPit = Math.max(0, safetyMargin * input.avgFuelPerLap);

        // Try a small splash (30% of tank) + save enough to make it
        const splashAmount = input.fuelCapacity * 0.3;
        const totalFuelAfterSplash = fuelInTankAtPit + splashAmount;
        
        if (lapsAfterPit > 0) {
          const requiredFuelPerLap = totalFuelAfterSplash / lapsAfterPit;
          const savePercent = ((input.avgFuelPerLap - requiredFuelPerLap) / input.avgFuelPerLap) * 100;

          // Only viable if: save is mild (≤8%) and fuel after splash covers the laps
          if (savePercent > 0 && savePercent <= Math.min(maxFuelSave, 8) && requiredFuelPerLap > 0) {
            // The saving only applies to laps AFTER the splash
            const savingCost = this.calculateFuelSaveTimeCost(savePercent, lapsAfterPit, penaltyPerPercent);
            const totalCost = splashPitTime + savingCost;

            options.push({
              type: 'splash_and_save', totalTimeCost: totalCost, pitStops: 1,
              fuelSavePercent: savePercent, fuelSaveTimeCost: savingCost, pitTimeCost: splashPitTime,
              pitType: 'splash',
              description: `Splash (${splashAmount.toFixed(0)}L) on lap ${pitLap} + save ${savePercent.toFixed(1)}% after. ` +
                           `Pit: ${splashPitTime}s + saving: ${savingCost.toFixed(1)}s = ${totalCost.toFixed(1)}s total.`,
              fuelToAdd: splashAmount,
              targetFuelPerLap: requiredFuelPerLap,
              recommendedPitLap: pitLap,
            });
          }
        }
      }
    }

    // ── Option F: Full stop(s) at normal pace ───────────────────────────
    if (baselinePitStops >= 1) {
      const pitLap = input.currentLap + Math.max(1, Math.floor(currentFuelLaps - safetyMargin));
      
      if (pitLap < myTotalLaps) {
        const totalPitCost = baselinePitStops * fullPitTime;
        const lapsAfterPit = myTotalLaps - pitLap;
        const fuelInTankAtPit = Math.max(0, safetyMargin * input.avgFuelPerLap);

        let fuelToAddAtPit: number;
        if (baselinePitStops === 1) {
          // Single stop: add exactly what's needed
          fuelToAddAtPit = Math.min(
            input.fuelCapacity - fuelInTankAtPit,
            Math.max(0, lapsAfterPit * input.avgFuelPerLap - fuelInTankAtPit)
          );
        } else {
          // Multi-stop: fill to 100% (tires are free), optimize stint length
          fuelToAddAtPit = input.fuelCapacity - fuelInTankAtPit;
        }

        options.push({
          type: 'full_stop', totalTimeCost: totalPitCost, pitStops: baselinePitStops,
          fuelSavePercent: 0, fuelSaveTimeCost: 0, pitTimeCost: totalPitCost,
          pitType: 'full',
          description: `${baselinePitStops} full stop(s) at normal pace. Pit lap ${pitLap}, add ${fuelToAddAtPit.toFixed(1)}L + tires. ` +
                       `Total pit cost: ${totalPitCost.toFixed(0)}s.`,
          fuelToAdd: fuelToAddAtPit,
          targetFuelPerLap: null,
          recommendedPitLap: pitLap,
        });
      }
    }

    // ── Option G: Mixed pit — (N-1) full stops + 1 splash ──────────────
    // For multi-stop strategies, check if the last stop can be a cheap splash
    // instead of a full stop. This saves (fullPitTime - splashPitTime) on the
    // last stop at no lap time penalty.
    //
    // How it works:
    // - First (N-1) stops: fill to 100% (free tires, full stint length)
    // - Last stop: splash just enough fuel to finish (shorter pit, no tires)
    // - The key constraint: fuel needed at the last stop must be ≤ 50% of tank
    //
    // Example: 2 stops remaining, maxStint = 30 laps, 55 laps to go
    //   Full plan: 2 × 55s = 110s pit cost
    //   Mixed plan: 1 × 55s + 1 × 30s = 85s pit cost → saves 25s!
    if (baselinePitStops >= 2) {
      const pitLap = input.currentLap + Math.max(1, Math.floor(currentFuelLaps - safetyMargin));
      
      if (pitLap < myTotalLaps) {
        // Total fuel available with N stops where first (N-1) are full refuels:
        // currentFuel + (N-1) × fuelCapacity + splashAmount = lapsRemaining × avgFuelPerLap
        const fullRefuels = baselinePitStops - 1;
        const totalFuelFromFullStops = input.currentFuel + fullRefuels * input.fuelCapacity;
        const totalFuelNeeded = lapsRemaining * input.avgFuelPerLap;
        const splashAmount = totalFuelNeeded - totalFuelFromFullStops;

        // Splash is viable only if we need a small top-up (≤ 50% of tank)
        if (splashAmount > 0 && splashAmount <= input.fuelCapacity * 0.5) {
          const mixedPitCost = fullRefuels * fullPitTime + splashPitTime;
          const savings = baselinePitStops * fullPitTime - mixedPitCost;

          options.push({
            type: 'full_stop', totalTimeCost: mixedPitCost, pitStops: baselinePitStops,
            fuelSavePercent: 0, fuelSaveTimeCost: 0, pitTimeCost: mixedPitCost,
            pitType: 'full', // first stop is full
            description: `${fullRefuels} full stop(s) + 1 splash. Fill 100% first, then splash ${splashAmount.toFixed(1)}L at last stop. ` +
                         `Pit cost: ${mixedPitCost.toFixed(0)}s (saves ${savings.toFixed(0)}s vs all-full).`,
            fuelToAdd: input.fuelCapacity - Math.max(0, safetyMargin * input.avgFuelPerLap),
            targetFuelPerLap: null,
            recommendedPitLap: pitLap,
          });
        }
      }
    }

    // Sort by total time cost (cheapest first)
    options.sort((a, b) => a.totalTimeCost - b.totalTimeCost);

    return options;
  }

  // ─── Main Calculate Method ────────────────────────────────────────────

  /**
   * Calculate comprehensive live race strategy.
   * Evaluates all strategy options and recommends the one with lowest total time cost.
   */
  calculate(input: LiveStrategyInput): LiveStrategyResult {
    const emptyOption: StrategyOption = {
      type: 'no_action', totalTimeCost: 0, pitStops: 0,
      fuelSavePercent: 0, fuelSaveTimeCost: 0, pitTimeCost: 0,
      pitType: null, description: 'Insufficient data.',
      fuelToAdd: null, targetFuelPerLap: null, recommendedPitLap: null,
    };

    // Validate input
    if (!input.avgLapTime || !input.avgFuelPerLap || input.avgFuelPerLap <= 0) {
      return {
        totalRaceLaps: 0,
        myTotalLaps: 0,
        lapsRemaining: 0,
        raceProgress: 0,
        estimatedRaceTimeRemaining: 0,
        currentFuelLaps: 0,
        fuelNeededToFinish: 0,
        fuelDeficit: 0,
        needsFuelSaving: false,
        fuelSavingTarget: 0,
        targetFuelPerLap: 0,
        fuelSavingPercentage: 0,
        needsPitStop: false,
        pitStopsRemaining: 0,
        recommendedPitLap: null,
        fuelToAdd: null,
        pitStopType: null,
        bestStrategy: emptyOption,
        allStrategies: [emptyOption],
        status: 'insufficient_data',
        message: 'Not enough data yet. Complete a few laps to see strategy.',
      };
    }

    // Calculate correct lap counts per class (multi-class aware)
    const { leaderLaps, myLaps } = this.calculateMyTotalLaps(
      input.raceDurationMinutes,
      input.avgLapTime,
      input.isMultiClass,
      input.fastestClassLapTime
    );

    const totalRaceLaps = leaderLaps;
    const myTotalLaps = myLaps;
    const lapsRemaining = Math.max(0, myTotalLaps - input.currentLap);
    
    const raceProgress = myTotalLaps > 0
      ? Math.min(100, (input.currentLap / myTotalLaps) * 100)
      : 0;

    const estimatedRaceTimeRemaining = lapsRemaining * input.avgLapTime;

    // Fuel calculations
    const currentFuelLaps = input.currentFuel / input.avgFuelPerLap;
    const fuelNeededToFinish = lapsRemaining * input.avgFuelPerLap;
    const fuelDeficit = fuelNeededToFinish - input.currentFuel;

    // ── Evaluate all strategy options ────────────────────────────────────
    const allStrategies = this.evaluateStrategies(
      input, lapsRemaining, currentFuelLaps, fuelDeficit, myTotalLaps
    );

    const bestStrategy = allStrategies[0]; // Cheapest option (sorted by totalTimeCost)

    // ── Derive result fields from best strategy ─────────────────────────
    const needsFuelSaving = bestStrategy.fuelSavePercent > 0;
    const targetFuelPerLap = needsFuelSaving
      ? input.avgFuelPerLap * (1 - bestStrategy.fuelSavePercent / 100)
      : input.avgFuelPerLap;
    const fuelSavingTarget = input.avgFuelPerLap - targetFuelPerLap;
    const fuelSavingPercentage = bestStrategy.fuelSavePercent;

    const needsPitStop = bestStrategy.pitStops > 0;
    const pitStopsRemaining = bestStrategy.pitStops;
    const recommendedPitLap = bestStrategy.recommendedPitLap;
    const fuelToAdd = bestStrategy.fuelToAdd;
    const pitStopType = bestStrategy.pitType;

    // ── Build status and message ─────────────────────────────────────────
    let status: LiveStrategyResult['status'];
    let message: string = bestStrategy.description;

    if (bestStrategy.type === 'no_action') {
      status = 'ok';
    } else if (needsFuelSaving && !needsPitStop) {
      status = 'saving_required';
    } else {
      status = 'pit_required';
    }

    // Append comparison context if there are multiple viable options
    if (allStrategies.length > 1) {
      const secondBest = allStrategies[1];
      const timeDiff = secondBest.totalTimeCost - bestStrategy.totalTimeCost;
      if (timeDiff > 0) {
        message += ` (${timeDiff.toFixed(1)}s faster than next best: ${secondBest.type.replace(/_/g, ' ')})`;
      }
    }

    return {
      totalRaceLaps,
      myTotalLaps,
      lapsRemaining,
      raceProgress,
      estimatedRaceTimeRemaining,
      currentFuelLaps,
      fuelNeededToFinish,
      fuelDeficit,
      needsFuelSaving,
      fuelSavingTarget,
      targetFuelPerLap,
      fuelSavingPercentage,
      needsPitStop,
      pitStopsRemaining,
      recommendedPitLap,
      fuelToAdd,
      pitStopType,
      bestStrategy,
      allStrategies,
      status,
      message,
    };
  }

  // ─── Recommendation Methods (backward-compatible) ─────────────────────

  /**
   * Calculate if fuel saving is worth it vs making a pit stop.
   * Now leverages the pre-computed strategy comparison from calculate().
   * 
   * Key insight: Avg lap time already accounts for tire degradation over a stint,
   * so we don't need a separate tire factor. The only question is:
   * "Does the total time lost from fuel saving exceed the pit stop delta?"
   * 
   * Pit stop types considered:
   * - Full stop (100% fuel + tires): ~55-62s delta — tires are free with full refuel
   * - Splash & go (partial fuel, no tires): ~30-35s delta
   */
  getFuelSavingRecommendation(result: LiveStrategyResult, pitStopTime: number = 60): {
    recommendation: 'save' | 'pit' | 'ok';
    reason: string;
    timeDifference: number;
    maxLapTimeLoss?: number;
  } {
    if (result.status === 'ok' || result.status === 'insufficient_data') {
      return {
        recommendation: 'ok',
        reason: result.status === 'ok'
          ? 'No action needed. Current fuel is sufficient.'
          : 'Insufficient data.',
        timeDifference: 0,
      };
    }

    // Use the pre-computed strategy comparison
    const best = result.bestStrategy;

    if (best.fuelSavePercent > 0 && best.pitStops === 0) {
      // Best strategy is save-only (no pit)
      const pitOption = result.allStrategies.find(s => s.pitStops > 0);
      const timeSaved = pitOption ? pitOption.totalTimeCost - best.totalTimeCost : pitStopTime - best.totalTimeCost;
      const maxLapTimeLoss = result.lapsRemaining > 0 ? pitStopTime / result.lapsRemaining : 0;
      return {
        recommendation: 'save',
        reason: best.description,
        timeDifference: Math.max(0, timeSaved),
        maxLapTimeLoss,
      };
    }

    if (best.pitStops > 0) {
      const saveOption = result.allStrategies.find(s => s.type === 'save_only');
      const timeSaved = saveOption ? saveOption.totalTimeCost - best.totalTimeCost : 0;
      return {
        recommendation: 'pit',
        reason: best.description,
        timeDifference: Math.max(0, timeSaved),
      };
    }

    return {
      recommendation: 'ok',
      reason: 'Current strategy is optimal.',
      timeDifference: 0,
    };
  }

  /**
   * Enhanced fuel saving recommendation with class-specific analysis.
   * Uses carClass to apply correct lap time penalty and pit stop deltas.
   * Now leverages the full strategy comparison from calculate().
   * 
   * Call this instead of getFuelSavingRecommendation() when you have the full input.
   */
  getFuelSavingRecommendationForClass(result: LiveStrategyResult, input: LiveStrategyInput): {
    recommendation: 'save' | 'pit' | 'splash' | 'ok';
    reason: string;
    timeDifference: number;
    maxLapTimeLoss?: number;
  } {
    if (result.status === 'ok' || result.status === 'insufficient_data') {
      return {
        recommendation: 'ok',
        reason: result.status === 'ok' 
          ? 'No action needed. Current fuel is sufficient.' 
          : 'Insufficient data.',
        timeDifference: 0,
      };
    }

    const best = result.bestStrategy;
    const fullPitTime = input.pitStopTime ?? this.getDefaultPitStopTime(input.carClass);

    // Map strategy type to recommendation
    switch (best.type) {
      case 'no_action':
        return { recommendation: 'ok', reason: best.description, timeDifference: 0 };

      case 'save_only':
      case 'save_to_skip_stop': {
        // Find the next-best pit option for comparison
        const pitOption = result.allStrategies.find(s => s.pitStops > best.pitStops) 
                       ?? result.allStrategies.find(s => s.type === 'full_stop');
        const timeSaved = pitOption ? pitOption.totalTimeCost - best.totalTimeCost : 0;
        const maxLapTimeLoss = result.lapsRemaining > 0 ? fullPitTime / result.lapsRemaining : 0;
        return {
          recommendation: 'save',
          reason: best.description,
          timeDifference: Math.max(0, timeSaved),
          maxLapTimeLoss,
        };
      }

      case 'splash_only':
      case 'splash_and_save': {
        const altOption = result.allStrategies.find(s => s.type === 'full_stop' || s.type === 'save_only');
        const timeSaved = altOption ? altOption.totalTimeCost - best.totalTimeCost : 0;
        return {
          recommendation: 'splash',
          reason: best.description,
          timeDifference: Math.max(0, timeSaved),
        };
      }

      case 'full_stop': {
        const saveOption = result.allStrategies.find(s => s.type === 'save_only' || s.type === 'save_to_skip_stop');
        const timeSaved = saveOption ? saveOption.totalTimeCost - best.totalTimeCost : 0;
        return {
          recommendation: 'pit',
          reason: best.description,
          timeDifference: Math.max(0, timeSaved),
        };
      }

      default:
        return { recommendation: 'ok', reason: best.description, timeDifference: 0 };
    }
  }
}
